import { getAppConfig } from '@services/config';
import { apiHttpService } from '@services/http';

import { contractsRepository } from '@repositories/contracts';
import { personRepository } from '@repositories/person';

import { ContractType } from '@app-types/contract';
import { Oauth2UserType } from '@app-types/oauth2';
import { BuyerContextType, ContactDetailsType, PersonDetailsType } from '@app-types/person';
import { UserType, UserTypeEnum } from '@app-types/user';

const appConfig = getAppConfig();

export class PersonServices {
    private appConfig = appConfig;

    public async getSignedContracts(sub: Oauth2UserType['sub'], user: UserType): Promise<ContractType[]> {
        try {
            return await contractsRepository.getSignedContracts(sub, user);
        } catch {
            return [];
        }
    }

    public async getContract(user: UserType, id: ContractType['id']): Promise<ContractType | undefined> {
        type ResponseType = {
            _links: {
                titulaire: {
                    href: string;
                };
            };
        };

        let sub = user.sub;

        if (user.user_type === UserTypeEnum.CDC) {
            const headers = new Headers();
            headers.set('X-Oauth2-Habilit', 'DRC_CDC_SP');
            headers.set('X-Oauth2-Scope', 'ContratConsult');

            const response = await apiHttpService.get<ResponseType>(
                `${this.appConfig.refcli.url}/contrats/${id}`,
                undefined,
                {
                    headers: Object.fromEntries(headers.entries()),
                    ttl: Number.parseInt(this.appConfig.cache.ttl.products),
                    user,
                },
            );

            sub = response?._links?.titulaire?.href.split('/')[2] as string;
        }

        return await contractsRepository.getContract(sub, id, user);
    }

    public async getContactDetails(id: string, user: UserType): Promise<ContactDetailsType | undefined> {
        return await personRepository.getContactDetails(id, user);
    }

    public async getPersonDetails(id: string, user: UserType): Promise<PersonDetailsType | undefined> {
        return await personRepository.getPersonDetails(id, user);
    }

    public async getIdPuFromContract(user: UserType, contract?: string): Promise<string | undefined> {
        return await personRepository.getIdPuFromContract(user, contract);
    }

    public async getBuyerContext(user?: UserType, contract?: string): Promise<BuyerContextType> {
        if (!user) {
            return {};
        }

        const idPu = await this.getIdPuFromContract(user, contract);

        if (!idPu) {
            return { user };
        }

        const contact = await this.getContactDetails(idPu, user);

        return { user, idPu, address: contact?.address };
    }
}

export const personServices = new PersonServices();
